import { useCallback, useMemo, useRef } from 'react';
import { ILogContent, useCommentListInfiniteQuery } from 'api';
import { Comment } from 'models/Task';

const mergePages = (pages: ILogContent[]): Record<string, Comment[]> => {
  return pages.reduce(
    (acc, page) => {
      const comments = page.content?.[0]?.comments as Record<string, Comment[]> | undefined;
      if (comments) {
        Object.entries(comments).forEach(([key, value]) => {
          if (acc[key]) {
            acc[key] = acc[key].concat(value);
          } else {
            acc[key] = value;
          }
        });
      }
      return acc;
    },
    {} as Record<string, Comment[]>
  );
};

export const useComments = (props: { id?: string }) => {
  const { id } = props;
  const observerLastRef = useRef<IntersectionObserver | null>(null);
  const observerFirstRef = useRef<IntersectionObserver | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    isLoading,
  } = useCommentListInfiniteQuery({
    size: 25,
    id: id as string,
  });

  const lastLogRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingNextPage) return;
      if (observerLastRef.current) observerLastRef.current.disconnect();
      observerLastRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );
      observerLastRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const firstLogRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingPreviousPage) return;
      if (observerFirstRef.current) observerFirstRef.current.disconnect();

      observerFirstRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasPreviousPage && !isFetchingPreviousPage) {
            fetchPreviousPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );

      observerFirstRef.current.observe(node);
    },
    [isFetchingPreviousPage, hasPreviousPage, fetchPreviousPage]
  );

  const comments = useMemo(() => {
    return mergePages(data?.pages || []);
  }, [data]);
  return {
    comments,
    isLoading,
    isFetchingNextPage,
    lastLogRef,
    firstLogRef,
    scrollContainerRef,
  };
};
